import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, Button } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../hooks/redux";
import {
  fetchGroups,
  fetchGroupStatistics,
  deleteGroup,
} from "../store/slices/groupsSlice";
import { fetchCategories } from "../store/slices/categoriesSlice";
import EnhancedTable from "../components/common/EnhancedTable";
import GroupDialog from "../components/dialogs/GroupDialog";
import DeleteConfirmDialog from "../components/dialogs/DeleteConfirmDialog";
import StatusChip from "../utils/StatusChip";
import GroupAgentsViewModal from "../components/dialogs/GroupAgentsViewModal";
import GroupStatusChangeDialog from "../components/dialogs/GroupStatusChangeDialog";
import { useDebounce } from "../hooks/useDebounce";

interface Group {
  id: number;
  name: string;
  description?: string;
  noofusers: number;
  isprice: number | boolean;
  status: number | boolean;
}

const GroupsList: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const dispatch = useAppDispatch();
  const { groups, loading, statistics, pagination } = useAppSelector(
    (state: any) => state.groups
  );

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupAgentDialogOpen, setGroupAgentDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | "view">("add");
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false); // <-- add this
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [groupToChangeStatus, setGroupToChangeStatus] = useState<Group | null>(
    null
  );
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    status: "",
  });

  // Column definitions (neutral styling)
  const [columns, setColumns] = useState([
    {
      id: "name",
      label: "Name",
      minWidth: 200,
      sortable: true,
      draggable: true,
      align: "left" as const,
      format: (value: string, row: Group) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "text.secondary" }}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      id: "description",
      label: "Description",
      minWidth: 250,
      sortable: false,
      draggable: true,
      align: "left" as const,
      format: (value: string, row: Group) => (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ color: "text.secondary" }}
        >
          {row.description || <span>-</span>}
        </Typography>
      ),
    },
    // {
    //   id: "noofusers",
    //   label: "Users",
    //   minWidth: 110,
    //   align: "center" as const,
    //   sortable: true,
    //   draggable: true,
    //   format: (value: number, row: Group) => (
    //     <Typography
    //       variant="body2"
    //       fontWeight={600}
    //       sx={{ color: "text.secondary" }}
    //     >
    //       {row.noofusers || <span>-</span>}
    //     </Typography>
    //   ),
    // },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      align: "center" as const,
      // sortable: true,
      draggable: true,
      format: (value: number, row: Group) => <StatusChip status={row.status} />,
    },
  ]);

  // Ref to track if column order has been initialized
  const isInitialized = React.useRef(false);

  const debouncedGroupName = useDebounce(columnFilters.name, 500);

  const handleColumnReorder = (newColumns: any[]) => {
    setColumns(newColumns);
    localStorage.setItem(
      "groupsTableColumnOrder",
      JSON.stringify(newColumns.map((col: any) => col.id))
    );
  };

  React.useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedColumnOrder = localStorage.getItem("groupsTableColumnOrder");
    if (savedColumnOrder) {
      try {
        const savedIds = JSON.parse(savedColumnOrder);
        const reorderedColumns = savedIds
          .map((id: string) => columns.find((col) => col.id === id))
          .filter(Boolean);
        const existingIds = reorderedColumns.map((col: any) => col.id);
        const newColumns = columns.filter(
          (col: any) => !existingIds.includes(col.id)
        );
        setColumns([...reorderedColumns, ...newColumns]);
      } catch (error) {
        console.error("Error loading saved column order:", error);
      }
    }
  }, [columns]);

  useEffect(() => {
    dispatch(fetchGroupStatistics());
    dispatch(fetchCategories());
  }, [dispatch]);

  // Server-side pagination & sorting state
  const [currentPage, setCurrentPage] = useState<number>(pagination?.page || 1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    pagination?.items_per_page || 10
  );
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const loadGroups = React.useCallback(() => {
    const params: any = {
      page: currentPage,
      items_per_page: itemsPerPage,
    };
    if (sortField) params.sort = `${sortField} ${sortDirection}`;
    if (debouncedGroupName.trim()) {
      params["search"] = debouncedGroupName.trim();
    }
    if (columnFilters.status && columnFilters.status !== "all") {
      params["where[status]"] = columnFilters.status === "active" ? 1 : 0;
    }
    dispatch(fetchGroups(params));
  }, [
    dispatch,
    currentPage,
    itemsPerPage,
    sortField,
    sortDirection,
    debouncedGroupName,
    columnFilters,
  ]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const navigate = useNavigate();
  const handleViewGroup = (group: Group) => {
    if (group?.id) {
      navigate(`/group-agents/${group.id}`, { state: { group } });
    }
  };

  const handleColumnFilterChange = (columnId: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleEditGroup = (group: Group) => {
    setSelectedGroup(group);
    setDialogMode("edit");
    setGroupDialogOpen(true);
  };

  const handleDeleteGroup = (group: Group) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleStatusChange = (group: Group) => {
    setGroupToChangeStatus(group);
    setStatusChangeDialogOpen(true);
  };

  const handleCloseStatusChangeDialog = () => {
    setStatusChangeDialogOpen(false);
    setGroupToChangeStatus(null);
  };

  const handleConfirmDelete = async () => {
    if (groupToDelete) {
      setDeleteLoading(true); // <-- set loading true
      try {
        await dispatch(deleteGroup(groupToDelete.id)).unwrap();
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
        dispatch(fetchGroupStatistics());
        loadGroups();
        toast.success("Group deleted successfully!");
      } catch (error) {
        toast.error("Failed to delete group.");
      } finally {
        setDeleteLoading(false); // <-- set loading false
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setGroupToDelete(null);
    setDeleteLoading(false); // <-- reset loading
  };

  const handleAddGroup = () => {
    setSelectedGroup(null);
    setDialogMode("add");
    setGroupDialogOpen(true);
  };

  const handleGroupDialogClose = (shouldRefresh = false) => {
    setGroupDialogOpen(false);
    setSelectedGroup(null);
    if (shouldRefresh) {
      loadGroups(); // <-- use this instead of dispatch(fetchGroups({}))
      dispatch(fetchGroupStatistics());
    }
  };

  const handleStatusChanged = () => {
    loadGroups(); // reload groups after status change
    dispatch(fetchGroupStatistics());
  };

  // Use server-provided list; keep client filters optional
  const dataToDisplay = Array.isArray(groups) ? groups : [];

  const stats = [
    {
      title: "Total Groups",
      value: statistics.total,
      color: "primary" as const,
    },
    {
      title: "Active Groups",
      value: statistics.active,
      color: "success" as const,
    },
    {
      title: "Inactive Groups",
      value: statistics.deactive,
      color: "error" as const,
    },
  ];

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      {/* Header Card */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          p: 2,
          borderRadius: 3,
          background: "#fff",
          boxShadow: "0 4px 18px -4px rgba(0,0,0,0.12)",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={0.5}>
            Groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            View and manage user groups.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          onClick={handleAddGroup}
        >
          Add Group
        </Button>
      </Box>

      {/* Neutral Statistics Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            md: "repeat(3,1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        {stats.map((stat, idx) => (
          <Card
            key={idx}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              background: "#fff",
              position: "relative",
              overflow: "hidden",
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  stat.color === "primary"
                    ? "linear-gradient(120deg,rgba(25,118,210,.06),transparent)"
                    : stat.color === "success"
                    ? "linear-gradient(120deg,rgba(46,125,50,.08),transparent)"
                    : "linear-gradient(120deg,rgba(211,47,47,.08),transparent)",
                pointerEvents: "none",
              },
            }}
          >
            <CardContent sx={{ py: 2.2, px: 2.4 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  color: `${stat.color}.main`,
                }}
              >
                {stat.title}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                {stat.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Enhanced Table */}
      <EnhancedTable
        columns={columns}
        data={dataToDisplay}
        loading={loading}
        onView={handleViewGroup}
        onEdit={handleEditGroup}
        onDelete={handleDeleteGroup}
        onStatusChange={handleStatusChange}
        showStatusChangeCondition={() => true}
        showActions={true}
        emptyMessage="No groups found"
        enableColumnReordering={true}
        onColumnReorder={handleColumnReorder}
        columnFilters={columnFilters}
        onColumnFilterChange={handleColumnFilterChange}
        statusFilterOptions={{
          status: [
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
          ],
        }}
        enableStickyFilters
        showSelection={false}
        useExternalPagination
        pagination={pagination}
        onPageChange={(page: number) => setCurrentPage(page)}
        onItemsPerPageChange={(newItemsPerPage: number) => {
          setItemsPerPage(newItemsPerPage);
          setCurrentPage(1);
        }}
        useExternalSorting
        onSort={(field: string) => {
          if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
          } else {
            setSortField(field);
            setSortDirection("asc");
          }
          setCurrentPage(1);
        }}
        currentSortField={sortField}
        currentSortDirection={sortDirection}
        entityName="group"
      />

      {/* Group Dialog */}
      <GroupDialog
        open={groupDialogOpen}
        onClose={(refresh = false) => handleGroupDialogClose(refresh)}
        mode={dialogMode}
        group={selectedGroup}
      />

      {/* Group Agent Dialog */}
      <GroupAgentsViewModal
        open={groupAgentDialogOpen}
        onClose={() => setGroupAgentDialogOpen(false)}
        group={selectedGroup}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading} // <-- pass loading state
        title="Delete Group"
        message="Are you sure you want to delete this group?"
        itemName={groupToDelete?.name || ""}
        itemType="group"
      />

      {/* Group Status Change Dialog */}
      <GroupStatusChangeDialog
        open={statusChangeDialogOpen}
        onClose={handleCloseStatusChangeDialog}
        group={groupToChangeStatus}
        onStatusChanged={handleStatusChanged}
      />
    </Box>
  );
};

export default GroupsList;
